import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import axios from 'axios';
import { toast } from 'react-toastify';

const SavedSettings = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState([]);
  const navigate = useNavigate();

  const fetchAllSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        toast.error('Você precisa estar autenticado para ver as configurações.');
        return;
      }

      const response = await axios.get('/api/investment/settings/all', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && Array.isArray(response.data)) {
        setSettings(response.data);
      }
    } catch (error) {
      toast.error('Erro ao carregar configurações. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllSettings();
  }, [fetchAllSettings]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleUseForSimulation = async (settingId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Você precisa estar autenticado para criar simulações.');
        return;
      }
      
      toast.info('Criando simulação com a configuração selecionada...');

      const response = await axios.post('/api/simulation', {
        settings_id: settingId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let simulationId;
      
      // Tentar obter o ID da simulação de diferentes formas
      if (response.data && response.data.simulation && response.data.simulation.id) {
        simulationId = response.data.simulation.id;
      } else if (response.data && response.data.id) {
        simulationId = response.data.id;
      } else {
        toast.error('Erro ao criar simulação: ID não encontrado na resposta.');
        return;
      }
      
      toast.success('Simulação criada com sucesso!');
      
      // Usar navigate em vez de window.location para melhor compatibilidade com React Router
      navigate(`/reports?simulation=${simulationId}`);
    } catch (error) {
      toast.error('Erro ao criar simulação. Por favor, tente novamente.');
    }
  };

  const handleEdit = (settingId) => {
    navigate(`/setup?setting_id=${settingId}`);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col">
              <h2 className="page-title">Configurações de CENÁRIOS</h2>
              <div className="text-muted mt-1">
                Selecione uma configuração para utilizar em uma nova simulação
              </div>
            </div>
            <div className="col-auto ms-auto">
              <div className="btn-list">
                <a href="/setup" className="btn btn-primary d-none d-sm-inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Novo CENÁRIO
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="page-body">
        <div className="container-xl">
          {settings.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-settings" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z"></path>
                  <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"></path>
                </svg>
              </div>
              <p className="empty-title">Nenhuma configuração encontrada</p>
              <p className="empty-subtitle text-muted">
                Você ainda não possui configurações salvas. Crie uma nova configuração para começar.
              </p>
              <div className="empty-action">
                <a href="/setup" className="btn btn-primary">
                  Nova Configuração
                </a>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="table-responsive">
                <table className="table table-vcenter card-table">
                  <thead>
                    <tr>
                      <th>Valor Inicial (EUR)</th>
                      <th>Taxa de Lucro</th>
                      <th>Operações/Dia</th>
                      <th>Período (meses)</th>
                      <th>Data de Criação</th>
                      <th className="w-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {settings.map((setting) => (
                      <tr key={setting.id}>
                        <td>{setting.initial_amount.toLocaleString('pt-BR', {minimumFractionDigits: 2})}</td>
                        <td>{(setting.profit_rate * 100).toFixed(2)}%</td>
                        <td>{setting.operations_per_day}</td>
                        <td>{setting.simulation_months}</td>
                        <td className="text-muted">{formatDate(setting.created_at)}</td>
                        <td>
                          <div className="btn-list flex-nowrap">
                            <button className="btn btn-success btn-sm" onClick={() => handleUseForSimulation(setting.id)}>
                              Simular
                            </button>
                            <button className="btn btn-primary btn-sm" onClick={() => handleEdit(setting.id)}>
                              Editar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default SavedSettings; 